/**
 * /es/prd-documento-de-requisitos — PRD concept page (Spanish).
 * Links /es/roadmap-de-producto + /es/registro-de-decisiones.
 */
import { MarketingShell } from './MarketingShell';

export function EsPrd() {
  return (
    <MarketingShell
      currentLocale="es"
      hreflangPath="/prd-documento-de-requisitos"
      hreflangLocales={['es']}
      documentTitle="PRD: qué es el documento de requisitos de producto (+ plantilla) · Draftlize"
      documentDescription="Qué es un PRD, su estructura y una plantilla para copiar — y cómo mantenerlo vivo para que no se desactualice después de cada sprint."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guía · PRD</span>
          <h1 className="serif" style={{ fontSize: 'clamp(32px, 4.3vw, 54px)', lineHeight: 1.06, margin: '12px 0 24px' }}>
            El PRD (documento de requisitos<br />
            <em>de producto), explicado.</em>
          </h1>
          <p className="lede">
            Un <strong>PRD</strong> (Product Requirements Document / documento de requisitos de producto) define <em>qué</em> construir y <em>por qué</em>: el problema, el alcance, los requisitos y las decisiones que los sostienen, para que el equipo y las partes interesadas compartan la misma imagen. El punto débil llega después: en dos o tres sprints el PRD deja de coincidir con la realidad — se <em>desvía</em>. Aquí, su estructura, una plantilla, y cómo evitar esa desviación.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">La estructura de un PRD que aguanta.</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ol style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 22 }}>
            <li><strong>Problema y objetivos</strong> — el problema de quién, por qué ahora, qué resultado se busca.</li>
            <li><strong>Alcance (dentro / fuera)</strong> — lo que se deja fuera a propósito.</li>
            <li><strong>Requisitos</strong> — funcionales y no funcionales, priorizados.</li>
            <li><strong>Decisiones y compromisos</strong> — qué se valoró y por qué se descartó lo demás.</li>
            <li><strong>Preguntas abiertas y criterios de éxito</strong>.</li>
          </ol>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            Esta plantilla se copia tal cual. La parte más valiosa — y la que antes caduca — es la 4: las decisiones. Un PRD es solo la punta visible del <a href="/es/roadmap-de-producto" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>roadmap</a> y de las decisiones debajo.
          </p>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">El problema real: <em>la desviación del PRD.</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            Un PRD es correcto el día que se aprueba. Luego una reunión cambia un supuesto, un caso límite revierte una decisión — y el documento se queda quieto. Nadie mantiene a mano un documento de doce páginas. Semanas después, un agente de IA construye sobre un spec que ya no se sostiene.
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Los requisitos se vuelven tarjetas</h3>
            <p>Cada requisito y decisión pasa a ser una tarjeta localizable en vez de una línea en la página siete — con su justificación y responsable, encontrable por ID.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Si una decisión cambia, el PRD lo señala</h3>
            <p>Cuando una decisión posterior revierte una anterior, las tarjetas dependientes — y la parte afectada del PRD — se marcan «stale» solas. El documento no miente en silencio durante tres semanas.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Tu agente de IA lee el spec vigente</h3>
            <p>Claude Code o Cursor leen el PRD vía MCP — al día o señalado explícitamente, no un documento estático cuyo estado nadie conoce.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Un PRD es correcto el día que se aprueba. El problema son las seis semanas siguientes, en las que nadie lo actualiza.
        </blockquote>
        <cite>Convierte los requisitos en tarjetas y el PRD avisa solo cuando queda obsoleto. Eso es Draftlize.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Preguntas frecuentes.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: '¿PRD o especificación?', a: 'El PRD define «qué y por qué», a nivel superior; la especificación entra en «cómo construirlo». Si la decisión del PRD cambia, la spec debe cambiar, y esa conexión es clave.' },
            { q: '¿Quién lo escribe?', a: 'Normalmente el PM, junto con ingeniería y diseño. Lo esencial es que las decisiones internas queden rastreables y con su porqué.' },
            { q: '¿Qué extensión?', a: 'Basta con que se entiendan problema, alcance, requisitos y decisiones. Más que la longitud, importa poder detectar cuándo algo queda obsoleto.' },
            { q: '¿Cómo evito que se desactualice?', a: 'Haz que requisitos y decisiones sean tarjetas enlazadas, no párrafos congelados. Si una decisión cambia, Draftlize marca «stale» lo dependiente. Sigue con <link>.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>
                {f.a.replace(' Sigue con <link>.','')}
                {i === 3 && (<>{' Sigue con '}<a href="/es/registro-de-decisiones" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>registro de decisiones</a>{'.'}</>)}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Un PRD que no se desvía <em>— gratis con $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>Regístrate y recibe $5</span>
          <span>Paga solo por lo que usas</span>
          <span>El saldo nunca caduca</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Escribe tu PRD como tarjetas enlazadas y cada requisito se vuelve localizable. Si una decisión cambia, marca «stale» lo afectado — se acabó la desviación del PRD.
          </p>
          <a className="btn-primary" href="/">Empieza gratis con $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
